import { Observer, UserRole } from '@nextforce/api'
import { Icon, Text, makeStyles } from '@rneui/themed'
import { t } from 'app/i18l'
import { View } from 'react-native'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { UserAvatar } from 'shared/components/user/user_avatar'

export interface MemberInfoProps {
  user: Observer | undefined
  role: UserRole | undefined
  isCurrentUser: boolean
  onPressCall: () => void
  onPressChat: () => void
  onPressEdit: () => void
  onPressRemove: () => void
}

export function MemberInfoView({
  user,
  role,
  isCurrentUser,
  onPressCall,
  onPressChat,
  onPressEdit,
  onPressRemove,
}: MemberInfoProps) {
  const styles = useStyles()

  if (!user) {
    return null
  }

  return (
    <View style={styles.userInfoContainer}>
      <UserAvatar user={user} size={'large'} />
      <View style={styles.textContainer}>
        {user.firstName && user.lastName ? (
          <Text style={[styles.text, styles.name]}>
            {`${user.firstName} ${user.lastName}${
              isCurrentUser ? ` (${t('you')})` : ''
            }`}
          </Text>
        ) : null}
        <View style={styles.titleContainer}>
          {user?.title ? (
            <Text style={[styles.text, styles.title]}>{`${user.title}`}</Text>
          ) : null}
          {user.jobTitle ? (
            <Text
              style={[styles.text, styles.title]}
            >{`${user.jobTitle}`}</Text>
          ) : null}
        </View>
        <View style={styles.buttonRow}>
          <View style={styles.storeButtonContainer}>
            <PressableOpacity style={styles.button} onPress={onPressCall}>
              <Icon name="phone" type="feather" size={15} />
            </PressableOpacity>
            <Text style={styles.buttonLabel}>{t('call')}</Text>
          </View>
          {isCurrentUser ? null : (
            <View style={styles.storeButtonContainer}>
              <PressableOpacity style={styles.button} onPress={onPressChat}>
                <Icon name="message" type="ionicons" size={15} />
              </PressableOpacity>
              <Text style={styles.buttonLabel}>{t('message')}</Text>
            </View>
          )}
          <View style={styles.storeButtonContainer}>
            <PressableOpacity style={styles.button} onPress={onPressEdit}>
              <Icon name="user-edit" type="font-awesome-5" solid size={15} />
            </PressableOpacity>
            <Text style={styles.buttonLabel}>{t('edit')}</Text>
          </View>
          <View style={styles.storeButtonContainer}>
            <PressableOpacity style={styles.button} onPress={onPressRemove}>
              <Icon
                name="trash"
                type="feather"
                size={15}
                color={styles.dangerButton.color}
              />
            </PressableOpacity>
            <Text style={styles.buttonLabel}>
              {role === 'observer' ? t('remove') : t('delete')}
            </Text>
          </View>
        </View>
      </View>
    </View>
  )
}

const useStyles = makeStyles(theme => ({
  userInfoContainer: {
    alignItems: 'center',
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey7 : theme.colors.grey6,
    paddingTop: 15,
  },
  textContainer: {
    margin: 10,
    alignItems: 'center',
  },
  text: {
    marginTop: 2,
    fontSize: 16,
  },
  name: {
    fontSize: 18,
  },
  title: { fontSize: 16 },
  titleContainer: {
    marginBottom: 10,
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  storeButtonContainer: {
    alignItems: 'center',
    marginHorizontal: 10,
  },
  button: {
    padding: 10,
    borderWidth: 0.5,
    width: 50,
    height: 50,
    borderColor: theme.colors.grey3,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 15,
    backgroundColor: theme.colors.white,
  },
  dangerButton: { color: theme.colors.primary },
  buttonLabel: {
    fontSize: 12,
    marginTop: 5,
  },
}))
